// Third Parth
import React from 'react';
import { connect } from 'react-redux';

// Components
import { PrimaryButton, CancelButton } from '../../../components/buttons';
import { UnitInput } from '../../../components/inputs';
import BMOGIFYearAgeInputs from '../../../components/bmo-gif/BMOGIFYearAgeInputs';
import BMOGIFPortfolioDropdown from '../../../components/bmo-gif/BMOGIFPortfolioDropdown';

// Utils
import { BMOi18n, GIF_SCHEME_TYPES, WITHDRAWAL_TYPES, MIN_RRIF_AGE, MIN_LIF_AGE } from '../../../../utils/bmo-gif/constants';
import { getAge } from '../../../../utils/time';
import { FREQUENCIES, ACCOUNT_TYPES } from '../../../../utils/constants';
import { ErrorTooltip } from '../../../components/tooltips';
import BMOGIFScenario from '@/types/bmo-gif/bmo-gif-scenario';
import BMOGIFPortfolio from '@/types/bmo-gif/bmo-gif-portfolio';
import { ReduxState } from '@/redux/modules/types';
import User from '@/types/user';
import { PERMISSIONS, userHasPermission } from '@/utils/permissions';

type StateProps = {
  scenario: BMOGIFScenario;
  scenarioNumber: number;
  locale: string;
  portfolio: BMOGIFPortfolio;
  user: User;
};

type DispatchProps = {
  cancelScenarioEditing: () => void;
  saveScenario: () => void;
  setLumpSumWithdrawalScheme: (scheme: string) => void;
  setLumpSumWithdrawalAmount: (amount: number) => void;
  setLumpSumWithdrawalAt: (at: number) => void;
  setSystematicWithdrawalAmount: (amount: number) => void;
  setSystematicWithdrawalType: (type: string) => void;
  setSystematicWithdrawalScheme: (scheme: string) => void;
  setSystematicWithdrawalStart: (start?: number) => void;
  setSystematicWithdrawalEnd: (end?: number) => void;
  setSystematicWithdrawalYears: (years?: number) => void;
  setSystematicWithdrawalFrequency: (frequency: string) => void;
  setLumpSumMonth: (month?: number) => void;
  setSystematicWithdrawalMonthEnd: (month?: number) => void;
  setSystematicWithdrawalMonthStart: (month?: number) => void;
};

type Props = {
  accountType?: string;
  user: User | null | undefined;
} & StateProps &
  DispatchProps;

class EditBMOGIFComparisonScenario extends React.Component<Props> {
  renderLumpSumSection = () => {
    const {
      locale,
      scenario: { withdrawal },
      portfolio: { dob, purchaseDate },
    } = this.props;

    if (!withdrawal) {
      return null;
    }

    const baseAge = getAge(dob, undefined, purchaseDate);
    const {
      lumpSum: { amount, at, scheme, month },
    } = withdrawal;

    const options = [
      {
        value: GIF_SCHEME_TYPES.YEAR,
        label: BMOi18n[locale].EDIT_SCENARIO.IN_YEAR,
      },
      {
        value: GIF_SCHEME_TYPES.AGE,
        label: BMOi18n[locale].EDIT_SCENARIO.AT_AGE,
      },
    ];

    return (
      <div className="lump-sum-section d-flex flex-column">
        <div className="section-title">
          <span>{BMOi18n[locale].EDIT_SCENARIO.LUMP_SUM_WITHDRAWAL}</span>
        </div>

        <div className="d-flex">
          <div className="lump-sum-amount d-flex flex-column">
            <div className="input-title">
              <span>{BMOi18n[locale].EDIT_SCENARIO.LUMP_SUM_AMOUNT}</span>
            </div>

            <UnitInput
              type="number"
              unitSymbol="$"
              min={0}
              value={amount || ''}
              onValueChange={(value: string) => this.props.setLumpSumWithdrawalAmount(Number(value))}
            />
          </div>

          <div className="lump-sum-at d-flex flex-column">
            <BMOGIFPortfolioDropdown selected={scheme} onChange={this.props.setLumpSumWithdrawalScheme} options={options} tooltipDisabled />

            <div className="d-flex align-items-center">
              {scheme === GIF_SCHEME_TYPES.AGE && !!at && !!baseAge && at < baseAge && <ErrorTooltip message={BMOi18n[locale].EDIT_SCENARIO.INVALID_AGE} />}

              <UnitInput type="number" min={0} value={at || ''} onValueChange={(value: string) => this.props.setLumpSumWithdrawalAt(Number(value))} />
            </div>
          </div>
          {scheme === GIF_SCHEME_TYPES.YEAR && (
            <div className="lump-sum-month d-flex flex-column">
              <div className="input-title">
                <span>{BMOi18n[locale].EDIT_SCENARIO.MONTH}</span>
              </div>
              <UnitInput
                type="number"
                min={1}
                max={12}
                defaultValue="12"
                value={month}
                onValueChange={(value: string) => {
                  this.props.setLumpSumMonth(value ? Number(value) : undefined);
                }}
              />
            </div>
          )}
        </div>
      </div>
    );
  };

  renderSystematicSection = () => {
    const {
      locale,
      scenario: { withdrawal },
      accountType,
      user,
    } = this.props;

    if (!withdrawal) {
      return null;
    }
    const isNonRegistered = accountType === ACCOUNT_TYPES.TFSA || accountType === ACCOUNT_TYPES.NON_REGISTERED;
    let keys = isNonRegistered ? [WITHDRAWAL_TYPES.CUSTOM.toUpperCase()] : Object.keys(WITHDRAWAL_TYPES);

    // Temporary feature flag for release
    const userCanSelectLIFMax = userHasPermission(user, PERMISSIONS.BMO_GIF_LIF_MAX);
    keys = keys.filter(key => key !== WITHDRAWAL_TYPES.LIF_MAX || userCanSelectLIFMax);
    // End of temporary feature flag

    const systematicTypeOptions = keys.map(key => ({
      value: WITHDRAWAL_TYPES[key],
      label: BMOi18n[locale].WITHDRAWAL_TYPES[key],
    }));

    return (
      <div className="systematic-section d-flex flex-column">
        <div className="section-title">
          <span>{BMOi18n[locale].EDIT_SCENARIO.SYSTEMATIC_WITHDRAWAL}</span>
        </div>

        <div className="d-flex">
          <div className="systematic-type d-flex flex-column">
            <BMOGIFPortfolioDropdown
              title={BMOi18n[locale].EDIT_SCENARIO.TYPE}
              selected={withdrawal.systematic.type}
              onChange={this.props.setSystematicWithdrawalType}
              options={systematicTypeOptions}
              tooltipDisabled
            />
          </div>

          {!!withdrawal.systematic.type && withdrawal.systematic.type === WITHDRAWAL_TYPES.CUSTOM && (
            <div className="systematic-amount d-flex flex-column">
              <div className="input-title">
                <span>{BMOi18n[locale].EDIT_SCENARIO.SYSTEMATIC_AMOUNT}</span>
              </div>

              <UnitInput
                type="number"
                unitSymbol="$"
                min={0}
                value={withdrawal.systematic.amount || ''}
                onValueChange={(value: string) => this.props.setSystematicWithdrawalAmount(Number(value))}
              />
            </div>
          )}

          {withdrawal.systematic.type === WITHDRAWAL_TYPES.RRIF && this.renderRRIFLIFInputs()}

          {withdrawal.systematic.type === WITHDRAWAL_TYPES.LIF_MAX && this.renderLIFMaxInputs()}
        </div>

        {!!withdrawal.systematic.type && withdrawal.systematic.type === WITHDRAWAL_TYPES.CUSTOM && this.renderCustomInputs()}
      </div>
    );
  };

  renderRRIFLIFInputs = () => {
    const {
      locale,
      scenario: { withdrawal },
      portfolio: { dob, purchaseDate },
    } = this.props;

    if (!withdrawal) {
      return null;
    }

    const baseAge = getAge(dob, undefined, purchaseDate);
    const {
      systematic: { start },
    } = withdrawal;

    const tooltipMessage = (start || 0) < MIN_RRIF_AGE ? BMOi18n[locale].EDIT_SCENARIO.INVALID_AGE_65 : BMOi18n[locale].EDIT_SCENARIO.INVALID_AGE;

    return (
      <div className="start-age d-flex flex-column">
        <div className="input-title">
          <span>{BMOi18n[locale].EDIT_SCENARIO.START_AGE}</span>
        </div>

        <div className="d-flex align-items-center">
          {!!start && !!baseAge && (start < baseAge || start < MIN_RRIF_AGE) && <ErrorTooltip message={tooltipMessage} />}

          <UnitInput type="number" min={0} value={start || ''} onValueChange={(value: string) => this.props.setSystematicWithdrawalStart(Number(value))} />
        </div>
      </div>
    );
  };

  renderLIFMaxInputs = () => {
    const {
      locale,
      scenario: { withdrawal },
      portfolio: { dob, purchaseDate, province },
    } = this.props;

    if (!withdrawal) {
      return null;
    }

    const baseAge = getAge(dob, undefined, purchaseDate);
    const {
      systematic: { start, end },
    } = withdrawal;

    let startAgeTooltip;
    let endAgeTooltip;
    if (start && baseAge) {
      if (start < MIN_LIF_AGE) {
        startAgeTooltip = BMOi18n[locale].EDIT_SCENARIO.INVALID_AGE_CUSTOM.replace('{value}', MIN_LIF_AGE);
      }

      if (start < baseAge) {
        startAgeTooltip = BMOi18n[locale].EDIT_SCENARIO.INVALID_AGE;
      }

      if (end && end <= start) {
        endAgeTooltip = BMOi18n[locale].EDIT_SCENARIO.INVALID_END_AGE;
      }
    }

    // LIF Max rates is disabled for Québec residents as of 2025
    // https://www.retraitequebec.gouv.qc.ca/en/professionnels/cri_frv/Pages/modifications-frv-2025.aspx
    const isInQC = province === 'Quebec';

    return (
      <>
        <div className="start-age d-flex flex-column">
          <div className="input-title d-flex align-items-center">
            <span>{BMOi18n[locale].EDIT_SCENARIO.START_AGE}</span>
          </div>

          <div className="d-flex align-items-center">
            {startAgeTooltip && <ErrorTooltip message={startAgeTooltip} />}
            {isInQC && <ErrorTooltip message={BMOi18n[locale].LIF_MAX_DISABLED_QC} />}
            <UnitInput
              disabled={isInQC}
              type="number"
              min={0}
              value={start || ''}
              onValueChange={(value: string) => this.props.setSystematicWithdrawalStart(Number(value))}
            />
          </div>
        </div>
        <div className="end-age d-flex flex-column">
          <div className="input-title d-flex align-items-center">
            <span>{BMOi18n[locale].EDIT_SCENARIO.TO_AGE}</span>
          </div>

          <div className="d-flex align-items-center">
            {endAgeTooltip && <ErrorTooltip message={endAgeTooltip} />}
            <UnitInput
              disabled={isInQC}
              type="number"
              min={0}
              value={end || ''}
              onValueChange={(value: string) => this.props.setSystematicWithdrawalEnd(Number(value))}
            />
          </div>
        </div>
      </>
    );
  };

  renderCustomInputs = () => {
    const {
      locale,
      scenario: { withdrawal },
      portfolio,
    } = this.props;

    if (!withdrawal) {
      return null;
    }

    const frequencyValues = [
      {
        value: FREQUENCIES.MONTHLY,
        label: BMOi18n[locale].MONTHLY,
      },
      {
        value: FREQUENCIES.SEMI_ANNUALLY,
        label: BMOi18n[locale].SEMI_ANNUALLY,
      },
      {
        value: FREQUENCIES.ANNUALLY,
        label: BMOi18n[locale].ANNUALLY,
      },
    ];

    return (
      <div className="d-flex flex-column">
        <BMOGIFYearAgeInputs
          locale={locale}
          selectedScheme={withdrawal.systematic.scheme}
          start={withdrawal.systematic.start}
          end={withdrawal.systematic.end}
          numYears={withdrawal.systematic.numYears}
          term={portfolio.term}
          setNumYears={this.props.setSystematicWithdrawalYears}
          setStart={this.props.setSystematicWithdrawalStart}
          setEnd={this.props.setSystematicWithdrawalEnd}
          setSchemeType={this.props.setSystematicWithdrawalScheme}
          dob={portfolio.dob}
          purchaseDate={portfolio.purchaseDate}
          setMonthEnd={this.props.setSystematicWithdrawalMonthEnd}
          setMonthStart={this.props.setSystematicWithdrawalMonthStart}
          startMonth={withdrawal.systematic.startMonth}
          endMonth={withdrawal.systematic.endMonth}
          showMonths={withdrawal.systematic.frequency === FREQUENCIES.MONTHLY}
        />

        <BMOGIFPortfolioDropdown
          className="frequency"
          title={BMOi18n[locale].EDIT_SCENARIO.FREQUENCY}
          placeholder="--"
          selected={withdrawal.systematic.frequency}
          onChange={this.props.setSystematicWithdrawalFrequency}
          options={frequencyValues}
          tooltipDisabled
        />
      </div>
    );
  };

  validInputs = () => {
    const {
      scenario: { withdrawal },
      portfolio: { dob, purchaseDate },
    } = this.props;

    let lumpSumValid = false;
    let systematicValid = false;

    if (!withdrawal) {
      return false;
    }

    const baseAge = getAge(dob, undefined, purchaseDate);

    if (withdrawal.lumpSum.amount && baseAge) {
      const {
        lumpSum: { scheme, at, month },
      } = withdrawal;

      if (scheme === GIF_SCHEME_TYPES.AGE) {
        lumpSumValid = at >= baseAge;
      }

      if (scheme === GIF_SCHEME_TYPES.YEAR) {
        lumpSumValid = !!at && !!month && month >= 1 && month <= 12;
      }
    }

    if (withdrawal.systematic.type && baseAge) {
      const {
        systematic: { type, frequency, start, end, amount, startMonth, endMonth },
      } = withdrawal;

      if (type === WITHDRAWAL_TYPES.CUSTOM) {
        if (withdrawal.systematic.scheme === GIF_SCHEME_TYPES.AGE) {
          systematicValid = !!amount && !!frequency && (start || 0) >= baseAge && end >= (start || 0);
        }
        if (withdrawal.systematic.scheme === GIF_SCHEME_TYPES.YEAR) {
          systematicValid =
            !!amount &&
            !!frequency &&
            start !== undefined &&
            !!startMonth &&
            !!endMonth &&
            !!end &&
            startMonth <= endMonth &&
            startMonth <= 12 &&
            startMonth >= 1 &&
            endMonth <= 12 &&
            endMonth >= 1;
        }
      }

      if (type === WITHDRAWAL_TYPES.RRIF) {
        systematicValid = (start || 0) >= baseAge && (start || 0) >= MIN_RRIF_AGE;
      }

      if (type === WITHDRAWAL_TYPES.LIF_MAX) {
        systematicValid = (start || 0) >= baseAge && (start || 0) >= MIN_LIF_AGE && (!end || end > (start || 0));
      }
    }
    if (withdrawal.lumpSum.amount && withdrawal.systematic.amount) {
      return lumpSumValid && systematicValid;
    }
    return lumpSumValid || systematicValid;
  };

  render() {
    const { scenarioNumber, locale } = this.props;

    return (
      <div className="edit-bmo-gif-comparison-scenario">
        <div className="d-flex">
          <div className="scenario-title">
            <span>{`Scenario ${scenarioNumber}`}</span>
          </div>

          <CancelButton onClick={this.props.cancelScenarioEditing} />

          <PrimaryButton disabled={!this.validInputs()} onClick={this.props.saveScenario}>
            {BMOi18n[locale].EDIT_SCENARIO.APPLY}
          </PrimaryButton>
        </div>

        {this.renderLumpSumSection()}

        {this.renderSystematicSection()}
      </div>
    );
  }
}

const mapStateToProps = (state: ReduxState) => ({
  user: state.auth.user,
});

export default connect(mapStateToProps)(EditBMOGIFComparisonScenario);
